<template>
  <div>
    <div style="display: flex;">
      <div>
        <div class="file-info">
          <div>
            <div>ファイル名</div>
            <div style="font-size: var(--font-size-normal)">{{ filename }}</div>
          </div>
          <div>
            <div>サイズ</div>
            <div>{{ fileStats.size | filesize }}</div>
          </div>
          <div>
            <div>データ件数</div>
            <div>{{ fileStats.numRecords | formatNumeric('0,0') }}</div>
          </div>
          <div>
            <div :class="{ requiresAttention: fileStats.numErrorRecords > 0 }">
              エラー件数
            </div>
            <div :class="{ requiresAttention: fileStats.numErrorRecords > 0 }">
              {{ fileStats.numErrorRecords | formatNumeric('0,0') }}
            </div>
          </div>
          <slot :digest="fileStats.digest" />
        </div>
        <div class="button-group" v-if="!required">
          <gl-button class="button-group__button" dense @click="clear"
            >Clear</gl-button
          >
        </div>
      </div>
      <div>
        <div>
          <a v-if="!cannotUpload" href="#" @click.prevent="$refs.file.click()">
            {{ uploadText }}
          </a>
          <div v-else class="uploading-text animated flash infinite">
            {{ uploadText }}{{ uploadProgressText }}
          </div>
        </div>
        <div style="margin-top: var(--spacing-small)">
          <gl-sub-text>
            または、アップロード済ファイルから選択する
          </gl-sub-text>
        </div>
        <div v-if="fetchingFiles">
          <spinner :diameter="20" :stroke="2"></spinner>
        </div>
        <div v-for="f in files" :key="f.id">
          <a
            v-if="!f.expired"
            href="#"
            @click.prevent="selectExistingFile(f.id)"
            >{{ f.filename }}</a
          >
          <span v-else class="filename-expired">
            {{ f.filename }}&nbsp;(期限切れ)
          </span>
          <gl-sub-text>（{{ f.uploadedAt | fileTimestamp }}）</gl-sub-text>
        </div>
      </div>
    </div>
    <input
      type="file"
      ref="file"
      accept=".txt,.text,.csv,.zip"
      hidden
      @change="uploadFile($event.target.files)"
    />
    <div class="digest-table-container" v-if="!!id">
      <div>
        ファイル解析結果（抜粋）
        <span>
          <md-icon class="help-icon">help</md-icon>
          <md-tooltip md-direction="top">
            アップロードされたファイルの解析結果から数件ピックアップして表示しています。
          </md-tooltip>
        </span>
      </div>
      <table class="digest-table">
        <thead>
          <tr>
            <th
              v-for="s in fileStats.digest.schema"
              :key="s.colName"
              :datatype="s.type"
            >
              {{ s.displayName }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(r, rowIndex) in fileStats.digest.data" :key="rowIndex">
            <td
              v-for="s in fileStats.digest.schema"
              :key="s.colName"
              :datatype="s.type"
            >
              {{ r[s.colName] }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

import ErrorReport from '@/mixins/ErrorReport'
import GlSubText from './common/GlSubText.vue'
import Spinner from '@/components/Spinner.vue'

const STATE_NOT_UOLOADING = 'NOT_UOLOADING'
const STATE_PREPARING = 'PREPARING'
const STATE_UPLOADING = 'UPLOADING'
const STATE_FILE_VARIFYING = 'FILE_VERIFYING'

const UPLOAD_TEXT = {
  [STATE_NOT_UOLOADING]: '新しいファイルをアップロードする',
  [STATE_PREPARING]: 'アップロード準備中…',
  [STATE_UPLOADING]: 'アップロード中…',
  [STATE_FILE_VARIFYING]: 'ファイル解析中…',
}

const FILE_TIMESTAMP_FORMAT = 'YYYY/MM/DD HH:mm:ss'

function hasExpired(now, expiresAt) {
  let expired = false
  if (expiresAt) {
    const _expiresAt = expiresAt * 1000
    expired = now > _expiresAt
  } else {
    expired = true
  }
  return expired
}

export default {
  components: { GlSubText, Spinner },
  mixins: [ErrorReport],
  filters: {
    fileTimestamp(v) {
      const milliseconds = v * 1000
      const date = new Date(milliseconds)
      return moment(date).format(FILE_TIMESTAMP_FORMAT)
    },
  },
  props: {
    id: {
      type: String,
    },
    filename: {
      type: String,
    },
    required: {
      type: Boolean,
      default: true,
    },
    dataType: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      // 選択ファイル
      file: undefined,
      files: [],
      fileStats: {
        size: 0,
        numRecords: 0,
        numErrorRecords: 0,
        digest: {},
      },
      state: STATE_NOT_UOLOADING,
      fetchingFiles: false,
      uploadProgress: 0,
    }
  },
  computed: {
    uploadText() {
      return UPLOAD_TEXT[this.state]
    },
    uploadProgressText() {
      if (this.state !== STATE_UPLOADING) {
        return undefined
      }
      return `${this.uploadProgress}%`
    },
    cannotUpload() {
      return this.state !== STATE_NOT_UOLOADING
    },
    errors() {
      if (!this.required) {
        return []
      }
      if (this.id) {
        return []
      }
      return ['ファイルをアップロード/選択してください。']
    },
  },
  watch: {
    id: {
      immediate: true,
      async handler(id) {
        // TODO 有効期限切れのものが指定された場合 (条件復元などで可能性あり？)
        //      IDがない状態と同様にクリアする必要がある

        const defaultEntity = {
          id: undefined,
          filename: undefined,
          size: 0,
          total: 0,
          error: 0,
          digest: {},
        }

        let file

        if (id) {
          file = await this.$repositories
            .get('uploadFile')
            .fetchFile(id, this.dataType)
        } else {
          file = defaultEntity
        }

        if (
          file.expiresAt &&
          hasExpired(new Date().getTime(), file.expiresAt)
        ) {
          file = defaultEntity
        }

        this.fileStats.size = file.size
        this.fileStats.numRecords = file.total
        this.fileStats.numErrorRecords = file.error
        this.fileStats.digest = file.digest ? file.digest : {}

        this.$emit('update:id', file.id)
        this.$emit('update:filename', file.filename)
      },
    },
  },
  methods: {
    async uploadFile(files) {
      if (files.length === 0) {
        return
      }

      this.file = files[0]

      const filename = this.file.name

      try {
        this.state = STATE_PREPARING

        const repo = this.$repositories.get('uploadFile')

        const issueResult = await repo.issueUploadUrl(filename)

        this.state = STATE_UPLOADING
        await repo.upload(
          issueResult.url,
          this.file,
          this.dataType,
          this.onUploadProgress.bind(this),
        )

        this.state = STATE_FILE_VARIFYING
        try {
          await repo.loadFile(issueResult.id, this.dataType)
        } catch (error) {
          // ファイルダイアログで拡張子を絞っていますが、
          //   1. ユーザーが「全てのファイル」に変更して選択できる
          //   2. 圧縮ファイルの中身までは確認できない
          // のでこのエラーは発生する可能性があります。
          if (
            error.response.status === 400 &&
            error.response.data.code === 'NOT_SUPPORTED'
          ) {
            const msg =
              `アップロードされたファイルが解析できませんでした。` +
              `サポートされている形式のファイルかご確認ください（${
                error.response.data.supported
              }）。圧縮ファイルをアップロードした場合、中身のファイルが` +
              '正しい形式であることをご確認ください。'
            alert(msg)
            return
          }
          throw error
        }

        this.$emit('update:id', issueResult.id)

        this.fetchFiles()
      } finally {
        this.state = STATE_NOT_UOLOADING
        this.uploadProgress = 0
        this.$refs.file.value = ''
      }
    },
    onUploadProgress(e) {
      this.uploadProgress = parseInt(Math.round((e.loaded / e.total) * 100))
    },
    async fetchFiles() {
      this.fetchingFiles = true

      try {
        const repo = this.$repositories.get('uploadFile')
        const files = await repo.fetchFiles(this.dataType)

        const now = new Date().getTime()

        this.files = files.map(f => ({
          ...f,
          expired: hasExpired(now, f.expiresAt),
        }))
      } finally {
        this.fetchingFiles = false
      }
    },
    selectExistingFile(id) {
      this.$emit('update:id', id)
    },
    clear() {
      this.$emit('update:id', undefined)
    },
  },
  async mounted() {
    this.fetchFiles()
  },
}
</script>

<style lang="scss" scoped>
.uploading-text {
  color: #808080;
  text-decoration: none;
  cursor: wait;
}

.filename-expired {
  color: var(--color-secondary-text);
}

.file-info {
  width: 250px;
  font-size: var(--font-size-small);

  padding: var(--spacing-medium);
  margin-right: var(--spacing-medium);

  border: 1px solid var(--color-border);
  border-radius: var(--spacing-small);

  > div {
    display: flex;

    &:not(:first-child) {
      margin-top: var(--spacing-small);
    }

    div:nth-child(1) {
      flex: 0 0 80px;
      color: var(--color-secondary-text);
    }

    div:nth-child(2) {
      flex: 1;
      text-align: right;
      overflow-x: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.digest-table-container {
  margin-top: var(--spacing-small);
  padding: var(--spacing-medium);

  border: 1px solid var(--color-border);
  border-radius: var(--spacing-small);
}

.digest-table {
  border-collapse: collapse;

  margin-top: var(--spacing-small);

  thead {
    tr {
      border-bottom: 1px solid var(--color-border);
    }
  }

  th {
    font-size: var(--font-size-small);
    font-weight: normal;
  }

  td,
  th {
    padding-left: var(--spacing-small);
    padding-right: var(--spacing-small);

    &[datatype='STRING'] {
      text-align: left;
    }
    &[datatype='NUMERIC'] {
      text-align: right;
    }
  }
}

.help-icon {
  font-size: 16px !important;
  transform: translate(-5px, -5px);
}

.client-slot {
  margin-top: var(--spacing-small);
}

.requiresAttention {
  color: var(--color-warn) !important;
}
</style>
